<template>
  <UikitPageComponent
    class="osk-product-page"
    :error="error"
    :pending="pending"
    @retry="refresh"
  >
    <div class="osk-wrapper">
      <UikitBreadcrumbs
        class="osk-product-page__breadcrumbs"
        :items="breadcrumbs"
      />

      <Product
        v-if="productData"
        :product="productData"
      />
    </div>

    <BusinessProductsMoreFromThisSeller
      v-if="productData?.seller?.id"
      :seller-id="productData.seller.id"
      :product-id="productData.productId"
      :event-options="eventOptions"
    />

    <BusinessProductsRecentlyWatched
      :event-options="eventOptions"
    />
  </UikitPageComponent>
</template>

<script lang="ts">
import { defineComponent, ComponentPublicInstance } from 'vue';
import { RouteLocationNormalized } from 'vue-router';
import { AnalyticsEvents } from '~/types/analytics';
import { useProductService } from '~/services/product';
import { ProductDTORes } from '~/restAPI/data-contracts';
import { getCommunity } from '~/services/analytics/oskellyAnalytics';

interface IInstance extends ComponentPublicInstance {
  getPreviousRoute(from: RouteLocationNormalized): void
}

export default defineComponent({
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      const instance = vm as IInstance;

      if (from?.name && !(from.name as string)?.includes('product')) {
        if (typeof instance.getPreviousRoute === 'function') {
          instance.getPreviousRoute(from);
        }
      }
    });
  },
});
</script>
<script lang="ts" setup>
definePageMeta({
  middleware: ['product'],
});

const productPreviousPage = ref<RouteLocationNormalized | null>(null);
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const url = useRequestURL();

const forWho: Record<string, string> = {
  105: t('components.products.productPage.forWho.105'),
  2: t('components.products.productPage.forWho.2'),
  188: t('components.products.productPage.forWho.188'),
};

const routeProduct = route.params.product;
let productId = 0;

if (typeof routeProduct === 'string') {
  const urlSplitted = routeProduct.split('-');

  productId = parseInt(urlSplitted[urlSplitted.length - 1], 10);
}

const { getProduct } = useProductService();
const { getCart } = useCartStore();
const { isAuth } = useAccountStore();
const { isHydrating, $addEvent, $afBannerIsOpen } = useNuxtApp();
const { changeActiveMenuCategory } = useActiveMenuCategory();
const { getGenderCategoryByRoute } = useGender();

const {
  data: productData, pending, error, refresh,
} = useLazyAsyncData(() => Promise.all([
  getProduct(productId, { withSizeChart: true }),
  isAuth.value ? getCart() : null,
]).then(([res]) => {
  addProductViewAnalytics(res);
  return res;
}));

const productParentCategoryUrl = computed(() => productData.value?.parentCategories && productData.value?.parentCategories[0].url);

const seoTitle = computed(() => {
  if (!productData.value) return '';

  const { brand, shortSeoDescription, name } = productData.value;

  return `${brand?.name} ${shortSeoDescription ?? name}`;
});

const currentForWho = computed(() => (productData.value?.parentCategories?.[0]?.id ? forWho[productData.value.parentCategories[0].id] : ''));

const title = computed(() => {
  const finishText = 'купить в интернет магазине с доставкой';

  if (!productData.value) return finishText;

  const { productModel } = productData.value;
  const model = productModel?.name ? ` ${productModel.name}` : '';

  return `${seoTitle.value}${model} ${currentForWho.value} – ${finishText}`;
});

const parentCategories = computed(() => productData.value?.parentCategories ?? []);

const headComp = computed(() => ({
  title: title.value,
  bodyAttrs: {
    class: 'oskelly-2_0 osk-product-page-body',
  },
  meta: [
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1.0, maximum-scale=1',
    },
    {
      name: 'description',
      content: t('meta.description'),
    },
    {
      name: 'og:title',
      content: t('meta.title'),
    },
    {
      name: 'og:type',
      content: 'website',
    },
    {
      name: 'og:url',
      content: url.href + route.path,
    },
    {
      name: 'og:image',
      content: t('meta.image'),
    },
    {
      name: 'og:description',
      content: t('meta.description'),
    },
  ],
  // TODO: small primary image
  script: [
    {
      type: 'application/ld+json',
      children: useJsonld(() => ({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: seoTitle.value,
        image: productData.value?.images?.map((img) => ({
          '@type': 'ImageObject',
          contentUrl: img.path,
          description: productData.value?.description,
        })),
        description: productData.value?.description,
        sku: productData.value?.productId ? `${productData.value?.productId}` : undefined,
        brand: {
          '@type': 'Brand',
          name: productData.value?.brand?.name,
        },
        offers: {
          '@type': 'Offer',
          priceCurrency: 'RUB',
          price: productData.value?.price,
          itemCondition: productData.value?.conditionId === 1 ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition',
          availability: 'https://schema.org/InStock',
          url: `https://oskelly.ru${productData.value?.url}`,
        },
      })),
    },
  ],
}));

useHead(headComp);

const breadcrumbs = computed(() => {
  const name = productData.value?.name;
  const brand = productData.value?.brand;

  if (productData.value?.breadcrumbs) {
    return [
      ...(productData.value.breadcrumbs.map((item) => ({
        label: item.name ?? '',
        to: `/catalog/${item.url}`,
      }))),
      { label: `${name} ${brand?.name}` },
    ];
  }

  return [
    ...(parentCategories.value
      .map((item) => ({
        label: item.displayName ?? '',
        to: `/catalog/${item.url}`,
      }))),
    { label: `${name} ${brand?.name}` },
  ];
});

const eventOptions = computed(() => ({
  view: { product_id: productData.value?.productId, value: getCommunity(productData.value) },
  productClick: { id_item: productData.value?.productId, value: getCommunity(productData.value) },
  showAll: { product_id: productData.value?.productId, value: getCommunity(productData.value) },
  sliderArrows: { item_id: productData.value?.productId, value: getCommunity(productData.value) },
}));

function getPreviousRoute(from: RouteLocationNormalized) {
  productPreviousPage.value = from;
}

function addProductViewAnalytics(product: ProductDTORes) {
  if (process.server || $afBannerIsOpen.value) {
    return;
  }

  const routerBack = router.options.history.state.back as string;

  $addEvent(AnalyticsEvents.ProductView, {
    ...product,
    previousPage: routerBack?.includes('/products/') ? routerBack : productPreviousPage.value?.name,
  });
}

watch(() => $afBannerIsOpen.value, (value) => {
  if (!value) {
    addProductViewAnalytics(productData.value!);
  }
});

watch(() => productParentCategoryUrl.value, (value) => {
  if (value) {
    const newGender = getGenderCategoryByRoute(value);

    if (newGender) {
      changeActiveMenuCategory(newGender);
    }
  }
});

onMounted(() => {
  if (productParentCategoryUrl.value) {
    const newGender = getGenderCategoryByRoute(productParentCategoryUrl.value);

    if (newGender) {
      changeActiveMenuCategory(newGender);
    }
  }

  if (isHydrating) {
    addProductViewAnalytics(productData.value!);
  }
});

defineExpose({ getPreviousRoute });
</script>

<style lang="scss" scoped>
@import "~/assets/scss/settings/index";

.osk-product-page {
  margin-top: -15px;

  @include media-query(lg-and-up) {
    margin-top: 0;
  }

  &__breadcrumbs {
    display: none;
    margin-bottom: $indent-compact;

    @include media-query(lg-and-up) {
      display: flex;
      margin-bottom: $indent-mlarge;
    }
  }
}
</style>
